import { ContentType, ProfileUserResponse, TypeCounts, UserResponse } from "@authory-packages/core";
import { getDisplayName } from "./getDisplayName";

const TITLE_MAX_LENGTH = 60;
const ROLE_CLAUSE_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 160;
const FORMAT_MIN_SHARE = 0.15;
const MAX_FORMATS = 2;
const TITLE_SEPARATOR = " | ";
const FALLBACK_SUMMARY = "Portfolio and published work";

const ROLE_NOUNS = [
    "journalist",
    "photojournalist",
    "reporter",
    "writer",
    "copywriter",
    "ghostwriter",
    "editor",
    "columnist",
    "critic",
    "producer",
    "podcaster",
    "correspondent",
    "author",
    "novelist",
    "poet",
    "essayist",
    "blogger",
    "broadcaster",
    "presenter",
    "host",
    "photographer",
    "filmmaker",
    "translator"
];

const ROLE_NOUN_PATTERN = new RegExp(`\\b(?:${ROLE_NOUNS.join("|")})s?\\b`, "i");
const CLAUSE_BREAK_PATTERN = /[,;:!?|•·\n]|\.(?=\s|$)|\s[-–—]\s/;
const TRAILING_FILLER_PATTERN = /(?:\s+(?:a|an|and|at|by|for|from|in|of|on|the|to|with|&))+$/i;

const FORMAT_NOUNS: Record<ContentType, string> = {
    [ContentType.Article]: "articles",
    [ContentType.PodcastItem]: "podcasts",
    [ContentType.Video]: "videos",
    [ContentType.TextPost]: "posts",
    [ContentType.ImagePost]: "posts",
    [ContentType.Thread]: "posts",
    [ContentType.VideoPost]: "posts",
    [ContentType.ReplyPost]: "posts",
    [ContentType.PdfFile]: "PDFs",
    [ContentType.Audio]: "audio",
    [ContentType.Image]: "images",
    [ContentType.Book]: "books",
    [ContentType.Email]: "newsletters",
    [ContentType.Website]: "websites"
};

/**
 * Builds the portfolio title used when the owner has not set their own SEO title.
 * @param user The portfolio owner
 * @returns "[Name] | [role]", "[Name] | [formats]" or "[Name] | Portfolio and published work"
 */
export function getProfileDefaultTitle(user: UserResponse | ProfileUserResponse) {
    const name = getDisplayName(user).trim();
    const role = getBioRole(user.bio, TITLE_MAX_LENGTH - name.length - TITLE_SEPARATOR.length);

    if (role) return `${name}${TITLE_SEPARATOR}${role}`;

    const formats = getDominantFormats(user.typeCounts);

    return `${name}${TITLE_SEPARATOR}${formats ? capitalize(formats) : FALLBACK_SUMMARY}`;
}

/**
 * Builds the portfolio meta description used when the owner has not set their own.
 * @param user The portfolio owner
 * @returns The bio as plain text, or a formats summary when there is no bio
 */
export function getProfileDefaultDescription(user: UserResponse | ProfileUserResponse) {
    const bio = (user.bio || "").replace(/\s+/g, " ").trim();

    if (bio) return truncateWithEllipsis(bio, DESCRIPTION_MAX_LENGTH);

    const formats = getDominantFormats(user.typeCounts);

    return `${formats ? capitalize(formats) : FALLBACK_SUMMARY} by ${getDisplayName(user).trim()}.`;
}

/**
 * Extracts a role from the start of the bio, e.g. "Climate reporter" from "Climate reporter, based in Berlin."
 * @param bio The profile bio
 * @param maxLength The room left in the title after the name
 * @returns The role, or an empty string when the bio does not open with one that fits
 */
function getBioRole(bio: string, maxLength: number) {
    const clause = (bio || "")
        .trim()
        .split(CLAUSE_BREAK_PATTERN)[0]
        .replace(/^(?:i['’]m|i\s+am)\s+/i, "")
        .replace(/^an?\s+/i, "")
        .trim();

    if (!clause || clause.length > ROLE_CLAUSE_MAX_LENGTH || !ROLE_NOUN_PATTERN.test(clause)) return "";

    const role = truncateAtWord(clause, maxLength).replace(TRAILING_FILLER_PATTERN, "").trim();

    // A cut that drops the profession leaves a fragment that says nothing
    return ROLE_NOUN_PATTERN.test(role) ? capitalize(role) : "";
}

/**
 * Names the one or two content formats that make up a clear share of the portfolio.
 * @param typeCounts Item counts per content type
 * @returns e.g. "articles and podcasts", or an empty string when no format stands out
 */
function getDominantFormats(typeCounts: TypeCounts | undefined) {
    const counts = new Map<string, number>();

    for (const type of Object.values(ContentType)) {
        const count = typeCounts?.[type] ?? 0;
        if (count > 0) counts.set(FORMAT_NOUNS[type], (counts.get(FORMAT_NOUNS[type]) ?? 0) + count);
    }

    const total = [...counts.values()].reduce((sum, count) => sum + count, 0);

    if (!total) return "";

    return [...counts.entries()]
        .filter(([, count]) => count / total > FORMAT_MIN_SHARE)
        .sort(([, a], [, b]) => b - a)
        .slice(0, MAX_FORMATS)
        .map(([noun]) => noun)
        .join(" and ");
}

/**
 * Cuts text at the last whole word that fits.
 * @param text The text to shorten
 * @param maxLength The maximum length
 * @returns The shortened text, empty when not even the first word fits
 */
function truncateAtWord(text: string, maxLength: number) {
    if (text.length <= maxLength) return text;

    const cut = text.slice(0, maxLength + 1);
    const lastSpace = cut.lastIndexOf(" ");

    return lastSpace > 0 ? cut.slice(0, lastSpace).trim() : "";
}

/**
 * Shortens text to a maximum length at a word boundary, marking the cut with an ellipsis.
 * @param text The text to shorten
 * @param maxLength The maximum length, ellipsis included
 * @returns The text, shortened when it was too long
 */
function truncateWithEllipsis(text: string, maxLength: number) {
    if (text.length <= maxLength) return text;

    const cut = truncateAtWord(text, maxLength - 1) || text.slice(0, maxLength - 1);

    return `${cut.replace(/[\s,;:.]+$/, "")}…`;
}

/**
 * Uppercases the first character.
 * @param text The text to capitalize
 * @returns The text with an uppercase first character
 */
function capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
